import { motion } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt } from 'react-icons/fa';
import { FC, createElement } from 'react';
import { useChatBot } from '../../context/ChatBotContext';

interface ProjectCardProps {
    title: string;
    description: string;
    image: string;
    technologies: string[];
    githubUrl?: string;
    demoUrl?: string;
    isChatBot?: boolean;
}

export const ProjectCard: FC<ProjectCardProps> = ({
    title,
    description,
    image,
    technologies,
    githubUrl,
    demoUrl,
    isChatBot
}) => {
    const { openChat } = useChatBot();

    const handleDemoClick = (e: React.MouseEvent) => {
        if (isChatBot) {
            e.preventDefault();
            openChat();
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden"
        >
            <div className="relative h-48 overflow-hidden">
                <img
                    src={image}
                    alt={title}
                    className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-300"
                />
            </div>

            <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {title}
                </h3>

                <p 
                    className="text-gray-600 dark:text-gray-300 mb-4"
                    dangerouslySetInnerHTML={{ __html: description }}
                />

                <div className="flex flex-wrap gap-2 mb-4">
                    {technologies.map((tech) => (
                        <span
                            key={tech}
                            className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full"
                        >
                            {tech}
                        </span>
                    ))}
                </div>

                <div className="flex gap-4">
                    {githubUrl && (
                        <a
                            href={githubUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                        >
                            {createElement(FaGithub as any, { size: 20 })}
                            <span>Code</span>
                        </a>
                    )}
                    {demoUrl && (
                        <a
                            href={demoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={handleDemoClick}
                            className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                        >
                            {createElement(FaExternalLinkAlt as any, { size: 20 })}
                            <span>Demo</span>
                        </a>
                    )}
                </div>
            </div>
        </motion.div>
    );
}; 